import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { API, useAuth } from '../App';

/**
 * SubscriptionGate
 * Gates a route behind Player's Locker access (trial OR pro OR admin).
 * - Loading: shows spinner
 * - No user: redirects to /login
 * - No access: redirects to /locker/paywall (preserving where they came from)
 * - Has access: renders children inside LockerAccessContext (so descendants can show trial banner)
 */
const LockerAccessContext = createContext({ access: null, refresh: () => {} });

export const useLockerAccess = () => useContext(LockerAccessContext);

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]" data-testid="locker-gate-loading">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-[#737373]">Loading your locker...</p>
    </div>
  </div>
);

const SubscriptionGate = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAccess = useCallback(async () => {
    try {
      const { data } = await API.get('/locker/access');
      setAccess(data);
    } catch (err) {
      setAccess({ has_access: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      fetchAccess();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [authLoading, user, fetchAccess]);

  if (authLoading || loading) return <Spinner />;

  if (!user) {
    return <Navigate to="/login" replace state={{ gated: true, from: location.pathname }} />;
  }

  if (!access?.has_access) {
    return <Navigate to="/locker/paywall" replace state={{ from: location.pathname }} />;
  }

  return (
    <LockerAccessContext.Provider value={{ access, refresh: fetchAccess }}>
      {children}
    </LockerAccessContext.Provider>
  );
};

export default SubscriptionGate;
